import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api.js';
import { useStripePaymentElement } from '../Plans.js';
import { Callout } from '../Callout.js';
import { QueryState } from '../QueryState.js';
import { CreditPurchaseConfirmation } from './CreditPurchaseConfirmation.js';
import { creditAmount, packagePrice, vatRateLabel } from './format.js';
import { forgetCreditPurchase, pendingCreditPurchase, rememberCreditPurchase } from './sessionMemory.js';

/**
 * BUYING PROJECT CREDITS: a package, its price with VAT, the card, then the
 * server's word that it was paid (S3, design B.4 and C.9).
 *
 *   1. The packages, read from the server (`ai_credit_package`), never
 *      written down here. Face value is the price; VAT is added on top where it
 *      applies (P11), so a package reads "€10 excl. VAT".
 *   2. Continue starts the purchase. Only then is the buyer's VAT known, so the
 *      summary — credits, subtotal, VAT when there is any, total — is the
 *      server's answer, printed as it came.
 *   3. The card goes through the workspace's one Payment Element
 *      (`useStripePaymentElement`, shared with the plan purchase). A declined
 *      card stays on this step, on the same PaymentIntent, for another card.
 *   4. "Confirming your payment" until the purchase reads PAID: the card step
 *      and a redirect's return prove nothing (`CreditPurchaseConfirmation`).
 *
 * The purchase uuid is remembered for this tab BEFORE Stripe is called, so a
 * redirect-based payment method that leaves and comes back lands on the same
 * confirmation rather than on an unexplained balance.
 */
export function CreditPackagePicker({
  projectId,
  onPaid,
  onClose,
  onForbidden,
  testId = 'credit-package-picker',
}: {
  projectId: string;
  /** Once, after the purchase is PAID and the wallet has been refreshed. */
  onPaid?: () => void;
  onClose: () => void;
  /** The server refused this member a purchase: the caller stops offering one. */
  onForbidden?: () => void;
  testId?: string;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  // A purchase this tab was already confirming (a reload, or a redirect's
  // return) is answered first, before anything new is offered — and kept on
  // screen once its outcome is known, though the tab then forgets it.
  const [confirming, setConfirming] = useState<string | null>(() => pendingCreditPurchase(projectId));
  const [payError, setPayError] = useState<string | null>(null);
  const radios = useRef<(HTMLButtonElement | null)[]>([]);

  const packages = useQuery({
    queryKey: ['ai-credit-packages', projectId],
    queryFn: () => api.creditPackages(projectId),
    staleTime: 5 * 60_000,
  });

  const start = useMutation({
    mutationFn: (packageCode: string) => api.startCreditPurchase(projectId, packageCode),
    onSuccess: (started) => {
      setPayError(null);
      // Already decided (an idempotent replay of a paid purchase): straight to its outcome.
      if (started.status !== 'PENDING') setConfirming(started.purchaseUuid);
    },
    onError: (error: unknown) => {
      if (forbidden(error)) onForbidden?.();
      if (error instanceof ApiError && error.code === 'PACKAGE_NOT_AVAILABLE') void packages.refetch();
    },
  });

  const started = start.data;
  const card = useStripePaymentElement(started?.clientSecret, started?.publishableKey);

  const pay = useMutation({
    mutationFn: async (purchaseUuid: string) => {
      // Before Stripe is called: a redirect never returns control to this function.
      rememberCreditPurchase(projectId, purchaseUuid);
      await card.confirm();
    },
    onMutate: () => {
      setPayError(null);
    },
    onSuccess: (_result, purchaseUuid) => {
      setConfirming(purchaseUuid);
    },
    onError: (error: unknown, purchaseUuid) => {
      // Declined or not sent: the purchase is still PENDING and payable, and
      // nothing is being confirmed, so nothing is remembered.
      forgetCreditPurchase(projectId, purchaseUuid);
      setPayError(error instanceof Error ? error.message : 'That payment could not be completed');
    },
  });

  const watching = confirming;

  if (watching !== null) {
    return (
      <div className="credits-picker" data-testid={testId} data-step="confirming">
        <CreditPurchaseConfirmation
          projectId={projectId}
          purchaseUuid={watching}
          onPaid={onPaid}
          onDone={onClose}
          onRetry={() => {
            forgetCreditPurchase(projectId, watching);
            setConfirming(null);
            setChosen(null);
            start.reset();
            pay.reset();
          }}
        />
      </div>
    );
  }

  if (started !== undefined) {
    const vat = started.taxCents !== 0;

    return (
      <div className="credits-picker" data-testid={testId} data-step="pay">
        <p className="colh" style={{ margin: 0 }}>
          Pay for {creditAmount(started.creditsCents)} of project credits
        </p>

        <dl className="credits-summary" data-testid="credit-purchase-summary">
          <div>
            <dt>Credits</dt>
            <dd data-testid="credit-purchase-credits">{creditAmount(started.creditsCents)}</dd>
          </div>
          <div>
            <dt>Subtotal</dt>
            <dd data-testid="credit-purchase-subtotal">{creditAmount(started.subtotalCents)}</dd>
          </div>
          {vat ? (
            <div>
              <dt>VAT ({vatRateLabel(started.vatRateBps)})</dt>
              <dd data-testid="credit-purchase-vat">{creditAmount(started.taxCents)}</dd>
            </div>
          ) : null}
          <div className="credits-summary-total">
            <dt>Total</dt>
            <dd data-testid="credit-purchase-total">{creditAmount(started.totalCents)}</dd>
          </div>
        </dl>
        {vat ? (
          <p className="mm credits-note" data-testid="credit-purchase-vat-note">
            VAT included in the total.
          </p>
        ) : null}

        <div ref={card.mountPoint} style={{ marginTop: 16 }} data-testid="credit-stripe-element" />

        {(payError ?? card.loadError) !== null ? (
          <p className="mm credits-error" data-testid="credit-payment-error" role="alert">
            {payError ?? card.loadError}
          </p>
        ) : null}

        <div className="credits-actions">
          <button
            type="button"
            className="ctl"
            data-testid="credit-purchase-back"
            disabled={pay.isPending}
            onClick={() => {
              start.reset();
              setPayError(null);
            }}
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="credit-payment-submit"
            disabled={pay.isPending || started.clientSecret === ''}
            onClick={() => {
              pay.mutate(started.purchaseUuid);
            }}
          >
            {pay.isPending ? 'Paying…' : `Pay ${creditAmount(started.totalCents)}`}
          </button>
        </div>
      </div>
    );
  }

  const list = packages.data ?? [];

  return (
    <div className="credits-picker" data-testid={testId} data-step="choose">
      <p className="colh" style={{ margin: 0 }}>
        Add project credits
      </p>
      <p className="mm credits-note">
        Credits belong to this production and are spent on its AI work. VAT is added where it applies;
        you see the total before you pay.
      </p>

      <QueryState query={packages} subject="the credit packages" testId="credit-packages" />

      {list.length > 0 ? (
        <div className="credits-packages" role="radiogroup" aria-label="Credit packages" data-testid="credit-packages">
          {list.map((item, index) => {
            const on = chosen === item.code;

            return (
              <button
                key={item.code}
                ref={(el) => {
                  radios.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={on}
                tabIndex={on || (chosen === null && index === 0) ? 0 : -1}
                className="credits-package"
                data-on={on ? '1' : '0'}
                data-testid={`credit-package-${item.code}`}
                onClick={() => {
                  setChosen(item.code);
                }}
                onKeyDown={(e) => {
                  const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
                  if (step === 0) return;
                  e.preventDefault();
                  const next = (index + step + list.length) % list.length;
                  const target = list[next];
                  if (target !== undefined) setChosen(target.code);
                  radios.current[next]?.focus();
                }}
              >
                <span className="credits-package-amount">{creditAmount(item.creditsCents)}</span>
                <span className="credits-package-label">of project credits</span>
                <span className="credits-package-price" data-testid={`credit-package-${item.code}-price`}>
                  {packagePrice(item.priceCents)} excl. VAT
                </span>
              </button>
            );
          })}
        </div>
      ) : packages.isSuccess ? (
        <p className="mm credits-note" data-testid="credit-packages-empty">
          No credit packages are on sale right now.
        </p>
      ) : null}

      {start.isError ? <PurchaseRefusal error={start.error} /> : null}

      <div className="credits-actions">
        <button type="button" className="ctl" data-testid="credit-package-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="credit-package-continue"
          disabled={chosen === null || start.isPending}
          onClick={() => {
            if (chosen !== null) start.mutate(chosen);
          }}
        >
          {start.isPending ? 'Starting…' : 'Continue to payment'}
        </button>
      </div>
    </div>
  );
}

/** A refusal of buying at all, rather than of one package. */
function forbidden(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === 'PURCHASE_NOT_ALLOWED') return true;

  // A bare 403 is the permission gate (BILLING_PURCHASE); the coded 403s say more.
  return error.status === 403 && error.code !== 'PRODUCTION_READ_ONLY' && error.code !== 'TERMS_ACCEPTANCE_REQUIRED';
}

/**
 * Why a purchase could not start, as an offer where it is one (B.7: toned ok
 * or warn, never danger) and as a fault only where something failed.
 */
function PurchaseRefusal({ error }: { error: unknown }) {
  const code = error instanceof ApiError ? error.code : null;

  const [tone, title, body]: ['warn' | 'info' | 'danger', string, string] = forbidden(error)
    ? ['info', 'Only a producer can buy credits for this production', 'Ask a producer on the team to add credits under Plan & billing.']
    : code === 'PRODUCTION_READ_ONLY'
      ? ['warn', 'This production is read-only', 'Renew its plan to add credits and run AI again. Nothing on it is lost.']
      : code === 'PROJECT_ARCHIVED'
        ? ['info', 'This production is archived', 'Credits cannot be added to an archived production.']
        : code === 'AI_BILLING_UNAVAILABLE'
          ? ['warn', 'Buying credits is paused', 'Credits cannot be bought right now. Your existing credits are safe.']
          : code === 'VAT_RATE_MISSING'
            ? ['warn', 'Credits cannot be bought right now', 'Nothing was charged. Try again later.']
            : code === 'PACKAGE_NOT_AVAILABLE'
              ? ['warn', 'That package is no longer offered', 'Pick another package.']
              : [
                  'danger',
                  'The purchase could not be started',
                  error instanceof ApiError && error.status !== 0 && error.status < 500 ? error.message : 'Nothing was charged. Try again in a moment.',
                ];

  return (
    <div style={{ marginTop: 14 }}>
      <Callout tone={tone} title={title} testId="credit-purchase-start-error">
        {body}
      </Callout>
    </div>
  );
}
